import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Mail,
  Calendar,
  Tag,
  FileText,
  Copy,
  X,
  Paperclip,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
const UserQueries = () => {
  const { isDarkMode } = useTheme();
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveMessage, setResolveMessage] = useState("");

  const theme = {
    bg: isDarkMode ? "bg-black" : "bg-gray-50",
    cardBg: isDarkMode ? "bg-black" : "bg-white",
    textPrimary: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    border: isDarkMode ? "border-gray-800" : "border-gray-200",
    hover: isDarkMode ? "hover:bg-neutral-900" : "hover:bg-gray-50",
    inputBg: isDarkMode ? "bg-neutral-900" : "bg-gray-100",
    inputText: isDarkMode ? "text-white" : "text-gray-900",
    tableBg: isDarkMode ? "bg-neutral-900" : "bg-gray-50",
  };

  const API_BASE_URL =
    process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "10",
      });

      if (statusFilter !== "all") params.append("status", statusFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await fetch(
        `${API_BASE_URL}/support/admin/all?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.statusCode === 401 || data.message === "Unauthorized") {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      setQueries(data.data || []);
      setPagination({
        total: data.total || 0,
        totalPages: Math.ceil((data.total || 0) / 10),
        hasNextPage: currentPage < Math.ceil((data.total || 0) / 10),
        hasPrevPage: currentPage > 1,
      });
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching queries:", error);
      setQueries([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, categoryFilter, API_BASE_URL]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const handleViewDetails = async (queryId) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/support/${queryId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedQuery(data.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching query details:", error);
    }
  };

  const handleResolveQuery = async () => {
    if (!selectedQuery) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${API_BASE_URL}/support/admin/${selectedQuery._id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "resolved", message: resolveMessage }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setShowResolveModal(false);
      setShowDetailsModal(false);
      setResolveMessage("");
      fetchQueries();
    } catch (error) {
      console.error("Error resolving query:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
    document.body.removeChild(textArea);
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const isImageFile = (filename) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    return imageExtensions.includes(getFileExtension(filename));
  };

  const getAttachmentUrl = (attachment) => {
    if (attachment.startsWith("http://") || attachment.startsWith("https://")) {
      return attachment;
    }
    return `${API_BASE_URL}/${attachment}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      resolved: {
        bg: "bg-green-500/20",
        text: "text-green-500",
        icon: CheckCircle,
        label: "Resolved",
      },
      pending: {
        bg: "bg-orange-500/20",
        text: "text-orange-500",
        icon: Clock,
        label: "Pending",
      },
      "in-progress": {
        bg: "bg-blue-500/20",
        text: "text-blue-500",
        icon: AlertCircle,
        label: "In Progress",
      },
      open: {
        bg: "bg-purple-500/20",
        text: "text-purple-500",
        icon: AlertCircle,
        label: "Open",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      account: "bg-purple-500/20 text-purple-500",
      payment: "bg-blue-500/20 text-blue-500",
      technical: "bg-red-500/20 text-red-500",
      general: "bg-gray-500/20 text-gray-400",
      feedback: "bg-cyan-500/20 text-cyan-500",
      performance: "bg-orange-500/20 text-orange-500",
      privacy: "bg-pink-500/20 text-pink-500",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
          categoryColors[category] || categoryColors.general
        }`}
      >
        <Tag size={12} />
        {category}
      </span>
    );
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className={`min-h-screen ${theme.bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>
              User Queries
            </h1>
            <p className={`${theme.textSecondary} mt-1`}>
              Manage and respond to user support requests
            </p>
          </div>
          <button
            onClick={fetchQueries}
            className={`flex items-center gap-2 px-4 py-2 ${theme.cardBg} ${theme.textPrimary} rounded-lg border ${theme.border} ${theme.hover} transition-colors`}
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>

        {/* Queries Table */}
        <div
          className={`${theme.cardBg} rounded-lg border ${theme.border} overflow-hidden`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theme.tableBg}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Title
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    User
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Category
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Date
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme.border}`}>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <RefreshCw
                        className={`animate-spin ${theme.textSecondary} mx-auto`}
                        size={24}
                      />
                    </td>
                  </tr>
                ) : queries.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`px-6 py-12 text-center ${theme.textSecondary}`}
                    >
                      No queries found
                    </td>
                  </tr>
                ) : (
                  queries.map((query) => (
                    <tr key={query._id} className={theme.hover}>
                      <td
                        className={`px-6 py-4 text-sm ${theme.textPrimary} max-w-xs`}
                      >
                        <div className="font-medium truncate">
                          {query.title}
                        </div>
                        <div
                          className={`text-xs ${theme.textSecondary} truncate mt-1`}
                        >
                          {query.description}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textPrimary}`}
                      >
                        <div className="font-medium">
                          {query.userId?.username || "Unknown"}
                        </div>
                        <div className={`text-xs ${theme.textSecondary}`}>
                          {query.userId?.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getCategoryBadge(query.category)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(query.status)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}
                      >
                        {formatDate(query.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewDetails(query._id)}
                          className={`flex items-center gap-1 px-3 py-1.5 ${theme.inputBg} ${theme.textPrimary} rounded-lg ${theme.hover} transition-colors`}
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && queries.length > 0 && (
            <div
              className={`px-6 py-4 border-t ${theme.border} flex items-center justify-between`}
            >
              <div className={`text-sm ${theme.textSecondary}`}>
                Showing page {currentPage} of {pagination.totalPages} (
                {pagination.total} total)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage}
                  className={`p-2 ${theme.inputBg} rounded-lg ${theme.hover} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className={`px-4 py-2 ${theme.textPrimary}`}>
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`p-2 ${theme.inputBg} rounded-lg ${theme.hover} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedQuery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`${theme.cardBg} rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                  Query Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`${theme.textSecondary} text-2xl`}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <div className="flex items-start justify-between mb-2">
                  <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>
                    {selectedQuery.title}
                  </h3>
                  {getStatusBadge(selectedQuery.status)}
                </div>
                <div className="flex items-center gap-2">
                  {getCategoryBadge(selectedQuery.category)}
                </div>
              </div>

              <div
                className={`p-4 rounded-lg ${theme.inputBg} flex justify-between items-start gap-4`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={16} className={theme.textSecondary} />
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>
                      Description
                    </p>
                  </div>
                  <p className={`${theme.textPrimary} text-sm leading-relaxed`}>
                    {selectedQuery.description}
                  </p>
                </div>
                {selectedQuery.attachments &&
                  selectedQuery.attachments.length > 0 && (
                    <button
                      onClick={() => setShowAttachmentsModal(true)}
                      className="px-3 py-2 bg-transparent border border-orange-400 text-orange-400 font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-2 text-sm hover:bg-orange-400/10"
                    >
                      <Paperclip size={14} />
                      Attachments ({selectedQuery.attachments.length})
                    </button>
                  )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${theme.inputBg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <User size={16} className={theme.textSecondary} />
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>
                      User
                    </p>
                  </div>
                  <p className={`${theme.textPrimary} font-medium`}>
                    {selectedQuery.userId?.username}
                  </p>
                  <p className={`text-xs ${theme.textSecondary} mt-1`}>
                    Role: {selectedQuery.userId?.role}
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${theme.inputBg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Mail size={16} className={theme.textSecondary} />
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>
                      Email
                    </p>
                  </div>
                  <p className={`${theme.textPrimary} text-sm`}>
                    {selectedQuery.userId?.email}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${theme.inputBg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className={theme.textSecondary} />
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>
                      Created At
                    </p>
                  </div>
                  <p className={`${theme.textPrimary} text-sm`}>
                    {formatDate(selectedQuery.createdAt)}
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${theme.inputBg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className={theme.textSecondary} />
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>
                      Last Updated
                    </p>
                  </div>
                  <p className={`${theme.textPrimary} text-sm`}>
                    {formatDate(selectedQuery.updatedAt)}
                  </p>
                </div>
              </div>

              {selectedQuery.status !== "resolved" && (
                <div className={`pt-4 border-t ${theme.border}`}>
                  <button
                    onClick={() => setShowResolveModal(true)}
                    className="w-full relative overflow-hidden group flex items-center justify-center gap-2 px-5 py-3 font-semibold rounded-xl text-green-400 border border-green-400 transition-all duration-300 hover:text-white"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></span>
                    <span className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></span>
                    <span className="relative flex items-center gap-2 z-10">
                      <CheckCircle size={18} />
                      Mark as Resolved
                    </span>
                  </button>
                </div>
              )}
            </div>

            <div className={`p-6 border-t ${theme.border} text-right`}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachments List Modal */}
      {showAttachmentsModal && selectedQuery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`${theme.cardBg} rounded-lg w-1/3 h-3/5 flex flex-col overflow-hidden`}
          >
            <div
              className={`p-6 border-b ${theme.border} flex items-center justify-between flex-shrink-0`}
            >
              <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                Attachments ({selectedQuery.attachments?.length || 0})
              </h2>
              <button
                onClick={() => setShowAttachmentsModal(false)}
                className={`p-2 ${theme.hover} rounded-lg`}
              >
                <X size={24} className={theme.textPrimary} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {selectedQuery.attachments &&
              selectedQuery.attachments.length > 0 ? (
                <div className="space-y-3">
                  {selectedQuery.attachments.map((attachment, index) => {
                    const isImage = isImageFile(attachment);
                    const attachmentUrl = getAttachmentUrl(attachment);

                    return (
                      <a
                        key={index}
                        href={attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all block overflow-hidden ${
                          isDarkMode
                            ? "border-gray-700 hover:border-blue-500 hover:bg-blue-500/10"
                            : "border-gray-300 hover:border-blue-500 hover:bg-blue-50"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${
                              isDarkMode
                                ? "bg-gray-800 border border-gray-700"
                                : "bg-gray-100 border border-gray-300"
                            }`}
                          >
                            <FileText
                              size={20}
                              className={
                                isImage ? "text-blue-500" : "text-gray-500"
                              }
                            />
                          </div>
                          <span
                            className={`${theme.textPrimary} font-medium flex-1 truncate`}
                          >
                            {attachment}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {isImage ? "Image" : "File"}
                          </span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className={`text-center py-12 ${theme.textSecondary}`}>
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No attachments found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && selectedQuery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.cardBg} rounded-lg max-w-md w-full`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                Resolve Query
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className={`p-4 rounded-lg ${theme.inputBg} space-y-2`}>
                <div className="flex justify-between items-center">
                  <span className={theme.textSecondary}>Query ID:</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-sm ${theme.textPrimary}`}>
                      {selectedQuery._id}
                    </span>
                    <button
                      onClick={() => handleCopy(selectedQuery._id)}
                      className={`p-1 ${theme.hover} rounded`}
                    >
                      <Copy size={14} className={theme.textSecondary} />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className={theme.textSecondary}>User:</span>
                  <span className={theme.textPrimary}>
                    {selectedQuery.userId?.username}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={theme.textSecondary}>Category:</span>
                  {getCategoryBadge(selectedQuery.category)}
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${theme.textPrimary} mb-2`}
                >
                  Resolution Message (Optional)
                </label>
                <textarea
                  value={resolveMessage}
                  onChange={(e) => setResolveMessage(e.target.value)}
                  placeholder="Add a message about the resolution..."
                  rows={4}
                  className={`w-full px-4 py-2 ${theme.inputBg} ${theme.textPrimary} rounded-lg border ${theme.border} focus:outline-none focus:ring-2 focus:ring-green-500`}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResolveModal(false);
                    setResolveMessage("");
                  }}
                  disabled={actionLoading}
                  className={`flex-1 px-4 py-2 ${theme.inputBg} ${theme.textPrimary} rounded-lg border ${theme.border} ${theme.hover} transition-colors disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResolveQuery}
                  disabled={actionLoading}
                  className="flex-1 relative overflow-hidden group flex items-center justify-center gap-2 px-4 py-2 font-semibold rounded-lg text-white bg-green-500 hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Resolving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Confirm Resolution
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserQueries;
